import { CompanyTypes, ProductTypes, EProductType } from "../companies";
import {
  ECompanyInformationSection,
  TCompanyInformationSection,
} from "./ECompanyInformationSection";
import { schemes, zSchema } from "../schema-validation";
import { ECompanyOnboardingSection } from "./ECompanyOnboardingSection";
import { LEGAL_STRUCTURES } from "../odo/IOdoCompany";

export type TCompanyShareableLinkSectionTitleForEmail =
  | "agreements"
  | "canadian_payroll_onboarding";

export const companyShareableLinkSectionToEmailTitleMap: Record<
  TCompanyInformationSection,
  TCompanyShareableLinkSectionTitleForEmail
> = {
  [ECompanyInformationSection.AGREEMENTS]: "agreements",
  [ECompanyInformationSection.CANADIAN_PAYROLL_ONBOARDING]:
    "canadian_payroll_onboarding",
};

export const companyInformationSectionSchema = zSchema.object({
  name: zSchema.nativeEnum(ECompanyInformationSection),
});

export const companyShareableLinksTokenPayloadSchema = zSchema.object({
  companyID: schemes.companyID(),
  isShareableLink: zSchema.literal(true),
  sections: zSchema.array(companyInformationSectionSchema),
  testMode: zSchema.boolean(),
  whiteLabelID: zSchema.string(),
});

export const companyInformationCompanySchema = zSchema.object({
  company_id: schemes.companyID(),
  business_name: zSchema.string(),
  direct_deposit_form: zSchema.boolean().optional(),
  deposit_account_form: zSchema.boolean().optional(),
  electronic_communication_form: zSchema.boolean().optional(),
  patriot_act_form: zSchema.boolean().optional(),
  product_type: zSchema.nativeEnum(EProductType).optional(),
  puzzl_terms_conditions: zSchema.boolean().optional(),
  reporting_agent_form: zSchema.boolean().optional(),
  tax_information_authorization_form: zSchema.boolean().optional(),
  canadian_payroll_config: zSchema
    .object({
      provider: zSchema.string().optional(),
      company_id: zSchema.string().optional(),
      business_entity_id: zSchema.string().optional(),
      pay_schedule_id: zSchema.string().optional(),
      access_token: zSchema.string().nullable().optional(),
      access_token_expires_at: zSchema.date().nullable().optional(),
    })
    .nullable()
    .optional(),
  business_address: zSchema.string().optional(),
  business_city: zSchema.string().optional(),
  business_state: zSchema.string().optional(),
  business_zip: zSchema.string().optional(),
  business_phone: zSchema.string().optional(),
  email: zSchema.string().optional(),
  first_name: zSchema.string().optional(),
  last_name: zSchema.string().optional(),
});

export const companyInformationPayloadSchema = zSchema.object({
  company: companyInformationCompanySchema,
  logoURL: zSchema.string().nullish(),
  tokenPayload: companyShareableLinksTokenPayloadSchema,
});

export type TCompanyShareableLinksSection = zSchema.infer<
  typeof companyInformationSectionSchema
>;
export type TCompanyShareableLinksTokenPayload = zSchema.infer<
  typeof companyShareableLinksTokenPayloadSchema
>;
export type TCompanyInformationPayload = zSchema.infer<
  typeof companyInformationPayloadSchema
>;

export const companyOnboardingSectionSchema = zSchema.object({
  name: zSchema.nativeEnum(ECompanyOnboardingSection),
});

export const companyOnboardingShareableLinksTokenPayloadSchema = zSchema.object(
  {
    whiteLabelID: zSchema.string(),
    companyID: schemes.companyID().nullish(),
    isShareableLink: zSchema.literal(true),
    sections: zSchema.array(companyOnboardingSectionSchema),
    testMode: zSchema.boolean(),
    company_type: zSchema.enum(CompanyTypes),
    product_type: zSchema.enum(ProductTypes),
    webhook_correlation_id: zSchema.string().optional(),
    skip_migration: zSchema.boolean(),
  }
);

export const companyOnboardingCompanySchema = zSchema.object({
  company_id: schemes.companyID(),
  first_name: zSchema.string(),
  last_name: zSchema.string(),
  ssn: schemes.ssn(),
  dob: zSchema.string(),
  email: zSchema.string(),
  ownership_percentage: zSchema.number(),
  job_title: zSchema.string(),
  address: zSchema.string(),
  address_line2: zSchema.string().optional(),
  city: zSchema.string(),
  state: zSchema.string(),
  zip: zSchema.string(),
  business_name: zSchema.string(),
  business_ein: zSchema.string(),
  business_phone: zSchema.string(),
  legal_structure: zSchema.enum(LEGAL_STRUCTURES),
  business_address: zSchema.string(),
  business_city: zSchema.string(),
  business_state: zSchema.string(),
  business_zip: zSchema.string(),
  mail_address: zSchema.string(),
  mail_city: zSchema.string(),
  mail_state: zSchema.string(),
  mail_zip: zSchema.string(),
  physical_address: zSchema.string(),
  physical_city: zSchema.string(),
  physical_state: zSchema.string(),
  physical_zip: zSchema.string(),
  isPhysicalAddressSameAsBusiness: zSchema.boolean(),
  isMailingAddressDifferent: zSchema.boolean(),
  direct_deposit_form: zSchema.boolean(),
  deposit_account_form: zSchema.boolean(),
  electronic_communication_form: zSchema.boolean(),
  patriot_act_form: zSchema.boolean(),
  puzzl_terms_conditions: zSchema.boolean(),
  reporting_agent_form: zSchema.boolean(),
  tax_information_authorization_form: zSchema.boolean(),
  signature: zSchema.string(),
  product_type: zSchema.enum(ProductTypes).optional(),
});

export const companyOnboardingPayloadSchema = zSchema.object({
  company: companyOnboardingCompanySchema.nullish(),
  logoURL: zSchema.string().nullish(),
  tokenPayload: companyOnboardingShareableLinksTokenPayloadSchema,
});

export type TCompanyOnboardingShareableLinksSection = zSchema.infer<
  typeof companyOnboardingSectionSchema
>;
export type TCompanyOnboardingShareableLinksTokenPayload = zSchema.infer<
  typeof companyOnboardingShareableLinksTokenPayloadSchema
>;
export type TCompanyOnboardingPayload = zSchema.infer<
  typeof companyOnboardingPayloadSchema
>;
